import React, { useEffect, useState } from 'react';
import { useKeenSlider } from 'keen-slider/react';
import 'keen-slider/keen-slider.min.css';

import errorCover from '../images/errorCover.jpg';
import { SpeakerIcon, getUserLabel, getInitials, getAvatarColor } from './SearchLightGameCard';
import './SearchLightGameInfo.scss';


function SearchLightGameInfo({
  params,
  auth,
  home,
  getSearchLightGameInfo: loadSearchLightGameInfo,
  likeSearchLightGame: triggerLikeSearchLightGame,
  unlikeSearchLightGame: triggerUnlikeSearchLightGame,
}) {
  const { gameId } = params || {};
  const [hasRequested, setHasRequested] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [sliderLoaded, setSliderLoaded] = useState(false);
  const [sliderRef, instanceRef] = useKeenSlider({
    initial: 0,
    loop: true, // 支持循环滚动
    slides: {
      perView: 1,
      spacing: 0, // 图片左右紧贴
    },
    slideChanged(slider) {
      setCurrentSlide(slider.track.details.rel);
    },
    created() {
      setSliderLoaded(true);
    },
  });

  // 自动轮播：每隔 5 秒切换到下一张
  useEffect(() => {
    if (!sliderLoaded || !instanceRef.current) return undefined;

    const slider = instanceRef.current;
    const totalSlides = slider.track?.details?.slides?.length || 0;
    if (totalSlides <= 1) return undefined;

    const timer = setInterval(() => {
      slider.next();
    }, 6000);

    return () => {
      clearInterval(timer);
    };
  }, [sliderLoaded, instanceRef]);

  useEffect(() => {
    const handleBrowserBack = () => {
      window.location.replace('/searchlight');
    };

    // Insert an extra history entry so browser back on this page
    // consistently routes users to SearchLight home.
    window.history.pushState({ searchLightInfo: true }, '', window.location.href);
    window.addEventListener('popstate', handleBrowserBack);

    return () => {
      window.removeEventListener('popstate', handleBrowserBack);
    };
  }, []);

  useEffect(() => {
    if (!gameId) return undefined;
    setHasRequested(true);
    loadSearchLightGameInfo(gameId);
    return undefined;
  }, [gameId, loadSearchLightGameInfo]);

  const token = auth && auth.token;
  const currentUser = auth && auth.user;
  const currentUserId = currentUser && currentUser.id;
  const searchLightGameInfo = home && home.searchLightGameInfo ? home.searchLightGameInfo : {};
  const game = searchLightGameInfo.data || null;
  const loading = (gameId && !hasRequested) || searchLightGameInfo.isLoading === true;
  const error = searchLightGameInfo && searchLightGameInfo.errors ? searchLightGameInfo.errors.message : null;
  const likeLoading = searchLightGameInfo.isLikeUpdating === true;

  if (loading) {
    return (
      <main className="SearchLightGamePage">
        <div className="SearchLightGamePage__inner SearchLightGamePage__inner--center">
          <p className="SearchLightGamePage__status">Loading game…</p>
        </div>
      </main>
    );
  }

  if (error || !game) {
    return (
      <main className="SearchLightGamePage">
        <div className="SearchLightGamePage__inner SearchLightGamePage__inner--center">
          <p className="SearchLightGamePage__status">Game not found.</p>
        </div>
      </main>
    );
  }

  const {
    coverPortrait,
    cover,
    name,
    title,
    gameName,
    developers,
    developer,
    description,
    summary,
    longGameDesc,
    steamGameDescription,
    gallery = [],
    likedNumber,
    likeCount,
    likedUsers: gameLikedUsers,
  } = game;

  const imageSrc = coverPortrait || cover || errorCover;
  const displayTitle = name || title || gameName || '';
  const developerLabel = developers || developer || '';
  const aboutText =
    description || summary || longGameDesc || steamGameDescription || '';

  const likedUsers = Array.isArray(gameLikedUsers) ? gameLikedUsers : [];
  const likeDisplayCount =
    likeCount != null ? likeCount : likedNumber != null ? likedNumber : 0;

  const galleryImages = Array.isArray(gallery)
    ? gallery.filter((item) => !item.type || item.type === 'image')
    : [];

  const liked =
    !!currentUserId &&
    (likedUsers || []).some((u) => String(u.id) === String(currentUserId));

  const handleLikeClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!token) {
      const redirectTo = encodeURIComponent(
        `${window.location.pathname}${window.location.search}${window.location.hash || ''}`,
      );
      window.location.href = `/sign-in?redirectto=${redirectTo}`;
      return;
    }
    if (!game || !game.id) return;
    if (likeLoading) return;

    if (liked) {
      triggerUnlikeSearchLightGame(game.id, token, currentUserId);
      return;
    }
    triggerLikeSearchLightGame(game.id, token, currentUser);
  };

  return (
    <main className="SearchLightGamePage">
      <div className="SearchLightGamePage__inner">
        <section className="SearchLightGamePage__left">
          <div className="SearchLightGamePage__card">
            <div className="SearchLightGamePage__card-cover">
              <img
                src={imageSrc}
                alt={displayTitle || 'Game cover'}
                onError={(e) => {
                  e.target.src = errorCover;
                }}
              />
            </div>

            <button
              type="button"
              className={`SearchLightGamePage__shout-button${liked ? ' is-liked' : ''
                }`}
              onClick={handleLikeClick}
              disabled={likeLoading}
            >
              <SpeakerIcon
                className="SearchLightGamePage__shout-icon"
                size={24}
              />
              <span className="SearchLightGamePage__shout-text">Shout!</span>
            </button>

            {/* 点赞数量 + 头像，与外层卡片保持一致 */}
            <div className="SearchLightGamePage__card-likes">
              <span className="SearchLightGamePage__card-likes-count">{likeDisplayCount} Shouts</span>
              <div className="SearchLightGamePage__card-likes-avatars" aria-label="点赞用户">
                {(likedUsers || []).slice(0, 5).map((u) => {
                  const label = getUserLabel(u);
                  const initials = getInitials(label, u.id);
                  const color = getAvatarColor(u);
                  return (
                    <div
                      key={u.id}
                      className="SearchLightGamePage__card-likes-avatar"
                      style={{ backgroundColor: color }}
                      title={label}
                    >
                      {initials}
                    </div>
                  );
                })}
                {Array.isArray(likedUsers) && likedUsers.length > 5 && (
                  <div className="SearchLightGamePage__card-likes-avatar--more">
                    +{likedUsers.length - 5}
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        <section className="SearchLightGamePage__right">
          <h1 className="SearchLightGamePage__title">{displayTitle}</h1>

          {developerLabel && (
            <div className="SearchLightGamePage__meta">
              <h2 className="SearchLightGamePage__meta-label">Developers</h2>
              <p className="SearchLightGamePage__meta-value">
                {developerLabel}
              </p>
            </div>
          )}

          {aboutText && (
            <div className="SearchLightGamePage__about">
              <h2 className="SearchLightGamePage__meta-label">About</h2>
              <div
                className="SearchLightGamePage__about-text parsed-content"
                // `aboutText` 可能包含 HTML（例如来自后端的 <p>...），因此在详情页中以 HTML 方式渲染
                dangerouslySetInnerHTML={{ __html: aboutText }}
              />
            </div>
          )}


        </section>
      </div>
      {/* 先使用keen-slider组件实现轮播；没有 gallery 时留出固定高度占位 */}
      {galleryImages.length > 0 ? (
        <>
          <section
            className="SearchLightGamePage__gallery"
            aria-label="Game gallery"
          >
            <div
              ref={sliderRef}
              className="keen-slider SearchLightGamePage__gallery-track"
            >
              {galleryImages.map((item, index) => {
                const src = item.url || item.thumb || item;
                if (!src) return null;
                const order = index + 1;
                return (
                  <div
                    key={item.id || src || order}
                    className="keen-slider__slide SearchLightGamePage__gallery-item"
                  >
                    <img
                      src={src}
                      alt={
                        displayTitle
                          ? `${displayTitle} screenshot ${order}`
                          : `Screenshot ${order}`
                      }
                    />
                  </div>
                );
              })}
            </div>
          </section>
          {sliderLoaded && instanceRef.current && (
            <div className="SearchLightGamePage__gallery-controls">
              {(() => {
                const totalSlides =
                  instanceRef.current.track.details.slides.length;
                const hasMultiple = totalSlides > 1;
                return (
                  <>
                    {hasMultiple && <button
                      type="button"
                      className="SearchLightGamePage__gallery-arrow SearchLightGamePage__gallery-arrow--prev"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (hasMultiple) {
                          instanceRef.current?.prev();
                        }
                      }}
                      aria-label="Previous screenshots"
                    >
                    </button>
                    }
                    {hasMultiple && (
                      <div className="SearchLightGamePage__gallery-indicator">
                        {(() => {
                          const indicatorCount = Math.min(totalSlides, 6);
                          return Array.from(
                            { length: indicatorCount },
                            (_, idx) => {
                              const isLastDot =
                                indicatorCount === 6 &&
                                idx === indicatorCount - 1 &&
                                totalSlides > indicatorCount;
                              const isActive =
                                currentSlide === idx ||
                                (isLastDot && currentSlide >= idx);
                              return (
                                <button
                                  key={idx}
                                  type="button"
                                  onClick={() => {
                                    instanceRef.current?.moveToIdx(idx);
                                  }}
                                  className={`SearchLightGamePage__gallery-dot${isActive ? ' is-active' : ''
                                    }`}
                                  aria-label={`View screenshots page ${idx + 1
                                    }`}
                                />
                              );
                            },
                          );
                        })()}
                      </div>
                    )}
                    {hasMultiple && <button
                      type="button"
                      className="SearchLightGamePage__gallery-arrow SearchLightGamePage__gallery-arrow--next"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (hasMultiple) {
                          instanceRef.current?.next();
                        }
                      }}
                      aria-label="Next screenshots"
                    >
                    </button>
                    }
                  </>
                );
              })()}
            </div>
          )}
        </>
      ) : (
        <section
          className="SearchLightGamePage__gallery SearchLightGamePage__gallery--placeholder"
          aria-hidden="true"
        />
      )}
    </main>
  );
}

export default SearchLightGameInfo;

